import React, { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from './api';
import { IconEye, IconPeople } from './Icons';
import { Modal } from './Modal';
import { useToast } from './Toast';

type Nums = { paper: number; total: number; papers: number };
const fmt = (n: number) => n.toLocaleString('en-IN');

/**
 * Top-bar viewer numbers, next to the date: first this newspaper's viewers, then the total of every newspaper.
 * Each shared-link opening adds one to that paper. The admin can click to change either number.
 */
export function Viewers({ date, isAdmin }: { date: string; isAdmin: boolean }) {
  const toast = useToast();
  const [n, setN] = useState<Nums | null>(null);
  const [edit, setEdit] = useState(false);
  const [paperText, setPaperText] = useState('');
  const [totalText, setTotalText] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!date) return;
    api.paperViewers(date).then(setN).catch(() => undefined);
  }, [date]);

  useEffect(() => {
    setN(null);
    load();
    const t = window.setInterval(load, 60000);
    return () => window.clearInterval(t);
  }, [load]);

  if (!date || !n) return null;

  const open = () => {
    setPaperText(String(n.paper));
    setTotalText(String(n.total));
    setEdit(true);
  };
  const parse = (s: string) => (/^\d{1,12}$/.test(s.trim()) ? Number(s.trim()) : null);
  const run = async (fn: () => Promise<Nums>, ok: string) => {
    setBusy(true);
    try {
      const r = await fn();
      setN(r);
      setPaperText(String(r.paper));
      setTotalText(String(r.total));
      toast(ok, 'ok');
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not save.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const chips = (
    <>
      <span className="vw-chip" title="Viewers of this newspaper">
        <IconEye size={16} />
        <strong>{fmt(n.paper)}</strong>
      </span>
      <span className="vw-chip vw-total" title="Total viewers of all newspapers so far">
        <IconPeople size={16} />
        <strong>{fmt(n.total)}</strong>
      </span>
    </>
  );

  return (
    <>
      {isAdmin ? (
        <button className="viewers viewers-edit" onClick={open} title="Viewers – click to change" aria-label="Viewers – click to change">
          {chips}
        </button>
      ) : (
        <div className="viewers" aria-label="Viewers">
          {chips}
        </div>
      )}
      {edit && (
        <Modal title="Viewers" onClose={() => setEdit(false)}>
          <div className="vw-form">
            <label>
              <span>
                <IconEye size={16} /> This newspaper's viewers
              </span>
              <input inputMode="numeric" value={paperText} onChange={(e) => setPaperText(e.target.value)} />
            </label>
            <button
              className="btn primary"
              disabled={busy || parse(paperText) === null}
              onClick={() => run(() => api.setPaperViewers(date, parse(paperText) as number), 'This newspaper’s viewers saved.')}
            >
              Save
            </button>
            <label>
              <span>
                <IconPeople size={16} /> Total viewers (all {n.papers} newspapers)
              </span>
              <input inputMode="numeric" value={totalText} onChange={(e) => setTotalText(e.target.value)} />
            </label>
            <button
              className="btn primary"
              disabled={busy || parse(totalText) === null}
              onClick={() => run(() => api.setTotalViewers(date, parse(totalText) as number), 'Total saved and shared out over the newspapers.')}
            >
              Save
            </button>
          </div>
          <p className="muted small">
            Saving a new total spreads it over all the newspapers, each getting a different number, and replaces their present numbers. Each time someone opens a shared link, that newspaper goes up by 1.
          </p>
        </Modal>
      )}
    </>
  );
}
